import type { SubtitleGroup } from "./learning";

const BASE_COMPOSITION_WIDTH = 1080;
const OUTPUT_COMPOSITION_WIDTH = 720;
const OUTPUT_COMPOSITION_HEIGHT = 1280;
const LAYOUT_SCALE = OUTPUT_COMPOSITION_WIDTH / BASE_COMPOSITION_WIDTH;

export const MATERIAL_VIDEO_FONT_FAMILY =
  '"SF Pro Rounded", "SF Pro Text", "Hiragino Sans", "Hiragino Kaku Gothic ProN", "Yu Gothic", "PingFang SC", "Microsoft YaHei", system-ui, sans-serif';

function scaleLayoutValue(value: number) {
  return Math.round(value * LAYOUT_SCALE * 1000) / 1000;
}

export const MATERIAL_VIDEO_LAYOUT = {
  compositionWidth: OUTPUT_COMPOSITION_WIDTH,
  compositionHeight: OUTPUT_COMPOSITION_HEIGHT,
  backgroundColor: "#FAF9F6",
  video: {
    x: scaleLayoutValue(54),
    y: scaleLayoutValue(54),
    width: scaleLayoutValue(972),
    height: scaleLayoutValue(547),
    radius: scaleLayoutValue(34),
    backgroundColor: "#0f0f0f",
    progressHeight: scaleLayoutValue(12),
    progressTrackColor: "rgba(255,255,255,0.18)",
    progressFillStart: "rgba(255,255,255,0.74)",
    progressFillEnd: "rgba(255,255,255,0.96)",
  },
  subtitles: {
    left: scaleLayoutValue(68),
    right: scaleLayoutValue(68),
    top: scaleLayoutValue(640),
    bottom: scaleLayoutValue(44),
    centerAnchorRatio: 0.5,
    gap: scaleLayoutValue(20),
    revealOffsetY: scaleLayoutValue(10),
    revealOpacityStart: 0.5,
  },
  row: {
    borderRadius: scaleLayoutValue(44),
    paddingTop: scaleLayoutValue(42),
    paddingBottom: scaleLayoutValue(52),
    paddingLeftWithTimestamp: scaleLayoutValue(84),
    paddingLeftDefault: scaleLayoutValue(18),
    paddingRight: scaleLayoutValue(18),
    activeBackgroundColor: "rgba(252, 244, 248, 0.92)",
    scaleMin: 0.985,
    scaleMax: 1,
    opacityMin: 0.72,
    opacityMax: 1,
    timestampTop: scaleLayoutValue(52),
    timestampFontSize: scaleLayoutValue(24),
    timestampActiveColor: "#6b7280",
    timestampInactiveColor: "rgba(156, 163, 175, 0.72)",
  },
  word: {
    columnGap: scaleLayoutValue(14),
    rowGap: scaleLayoutValue(20),
    paddingX: scaleLayoutValue(10),
    paddingY: scaleLayoutValue(6),
    borderRadius: 999,
    highlightBackgroundColor: "#f9dfe8",
    highlightShadowColor: "rgba(229, 146, 175, 0.2)",
    textHighlightedColor: "#4b3346",
    textActiveRowColor: "#667085",
    textInactiveColor: "#98a2b3",
    textFontSize: scaleLayoutValue(46),
    furiganaFontSize: scaleLayoutValue(24),
    romajiFontSize: scaleLayoutValue(24),
    furiganaOffset: scaleLayoutValue(14),
    romajiOffset: scaleLayoutValue(16),
    textLetterSpacing: scaleLayoutValue(1.2),
    annotationLetterSpacing: scaleLayoutValue(0.4),
    focusScaleMin: 1.08,
    focusScaleMax: 1.18,
  },
  fallbackTitle: {
    fontSize: scaleLayoutValue(58),
    lineHeight: 1.12,
    color: "#2f2322",
  },
} as const;

export const MATERIAL_VIDEO_SUBTITLE_VIEWPORT_HEIGHT =
  MATERIAL_VIDEO_LAYOUT.compositionHeight -
  MATERIAL_VIDEO_LAYOUT.subtitles.top -
  MATERIAL_VIDEO_LAYOUT.subtitles.bottom;

export const MATERIAL_VIDEO_RENDER_CONFIG = {
  fps: 30,
  exportScale: 1,
  videoBitrate: 500_000,
  audioBitrate: 24_000,
  jpegQuality: 60,
  previewAcceptableTimeShiftInSeconds: 0.45,
  mediaCacheSizeInBytes: 32 * 1024 * 1024,
  maxRenderLambdas: 4,
} as const;

export type MaterialVideoHighlightState = {
  activeKeys: Set<string>;
  completedKeys: Set<string>;
};

export function getMaterialVideoActiveWordKey(
  groups: SubtitleGroup[],
  currentTime: number,
) {
  for (const group of groups) {
    const words = group.words || [];
    if (!words.length) continue;

    let selectedIndex = -1;
    for (let index = 0; index < words.length; index += 1) {
      const word = words[index];
      const prevWord = words[index - 1];
      const nextWord = words[index + 1];
      const leftBoundary =
        index === 0 ? word.start : (prevWord.end + word.start) / 2;
      const rightBoundary =
        index === words.length - 1 ? word.end : (word.end + nextWord.start) / 2;

      const inWindow =
        currentTime >= leftBoundary &&
        (index === words.length - 1
          ? currentTime <= rightBoundary
          : currentTime < rightBoundary);
      if (inWindow) {
        selectedIndex = index;
        break;
      }
    }

    if (selectedIndex < 0) {
      selectedIndex = words.findIndex(
        (word, index) =>
          currentTime >= word.start &&
          (index === words.length - 1
            ? currentTime <= word.end
            : currentTime < word.end),
      );
    }

    if (selectedIndex < 0) {
      const firstStart = words[0]?.start ?? 0;
      const lastEnd = words.at(-1)?.end ?? 0;
      if (currentTime >= firstStart && currentTime <= lastEnd) {
        let nearestIndex = 0;
        let nearestDistance = Number.POSITIVE_INFINITY;
        for (let index = 0; index < words.length; index += 1) {
          const word = words[index];
          const center = (word.start + word.end) / 2;
          const distance = Math.abs(currentTime - center);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
          }
        }
        selectedIndex = nearestIndex;
      }
    }

    if (selectedIndex >= 0) {
      return `${group.id}:${words[selectedIndex].id}`;
    }
  }

  return undefined;
}

export function formatMaterialVideoClock(value: number) {
  if (!Number.isFinite(value) || value <= 0) return "00:00";
  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function getMaterialVideoActiveGroupIndex(
  groups: SubtitleGroup[],
  time: number,
) {
  const exact = groups.findIndex(
    (group) => time >= group.start && time <= group.end,
  );
  if (exact >= 0) return exact;
  return groups.findLastIndex((group) => time >= group.start);
}

export function getMaterialVideoActiveGroupIndices(
  groups: SubtitleGroup[],
  time: number,
) {
  const indices: number[] = [];
  for (let index = 0; index < groups.length; index += 1) {
    const group = groups[index];
    if (time >= group.start && time <= group.end) {
      indices.push(index);
      if (indices.length >= 2) break;
    }
    if (group.start > time && indices.length > 0) break;
  }
  return indices;
}

export function computeMaterialVideoHighlightState(
  groups: SubtitleGroup[],
  currentTime: number,
  karaokeMode: boolean,
): MaterialVideoHighlightState {
  const activeKeys = new Set<string>();
  const completedKeys = new Set<string>();
  const activeWordKey = getMaterialVideoActiveWordKey(groups, currentTime);

  if (activeWordKey) {
    activeKeys.add(activeWordKey);
  }

  for (const group of groups) {
    const words = group.words || [];
    if (!words.length) continue;

    if (karaokeMode) {
      for (const word of words) {
        if (currentTime >= word.end) {
          completedKeys.add(`${group.id}:${word.id}`);
        }
      }
    }
  }

  return { activeKeys, completedKeys };
}

export function materialVideoHasCjk(text: string) {
  return /[\u3400-\u4dbf\u4e00-\u9fff々〆]/u.test(text);
}
